<template>
  <div class="container mx-auto p-4" v-if="isLoaded">
    <ConfiguracionesGenerales
      :evento="evento"
      :campos="camposRegistro"
      :campos-visible="camposVisible"
      :base-url="BASE_URL"
      @saved="onSaved"
      @save-error="onSaveError"
    />

    <!-- Mensaje personalizado y copia emisor -->
    <div class="col-12 mt-5 mensaje-personalizado">
      <h2>Personaliza el mensaje para los invitados</h2>
      <div class="switch-container mb-4 flex items-center">
        <span class="mr-2 text-sm font-medium text-gray-700 cursor-pointer"
          >¿Enviar copia de invitación al emisor?</span
        >
        <div class="relative cursor-pointer" @click="toggleCopiaEmisor">
          <div :class="switchBgClassCopia" class="switch-bg"></div>
          <div :class="switchDotClassCopia" class="switch-dot"></div>
        </div>
      </div>
      <RichTextEditor :nombreEvento="evento.nombre" :tokenEvento="route.params.token" />
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import RichTextEditor from '@/components/RichTextEditor.vue'
import ConfiguracionesGenerales from './ConfiguracionesGenerales/ConfiguracionesGenerales.vue'

const BASE_URL = '/api'
const route = useRoute()
const toast = useToast()

const isLoaded = ref(false)
const camposVisible = ref(false)

const camposRegistroBase = ref([])
const camposRegistro = ref([])

const evento = ref({
  token: route.params.token,
  nombre: '',
  fecha_inicio: '',
  fecha_fin: '',
  codigo: '',
  permitir_duplicados: false,
  evento_padre_id: null,
  familia: {},
  campos_heredados: [],
  copia_invitacion_emisor: false,
  tipo_asistente: 'asistente',
  auto_staff: '',
  auto_asistente: '',
  auto_expositor: '',
  auto_staff_en: '',
  auto_asistente_en: '',
  auto_expositor_en: '',
  sin_limites_carga_usuarios: false,
  api: {
    url_evento: '',
    usuario: '',
    acceso: '',
    contacto_nombre: '',
    contacto_telefono: '',
    contacto_email: ''
  }
})

const switchBgClassCopia = computed(() =>
  evento.value.copia_invitacion_emisor ? 'bg-green-500' : 'bg-gray-600'
)
const switchDotClassCopia = computed(() =>
  evento.value.copia_invitacion_emisor ? 'translate-x-9' : 'translate-x-1'
)

async function toggleCopiaEmisor() {
  evento.value.copia_invitacion_emisor = !evento.value.copia_invitacion_emisor
  try {
    const res = await fetch(`${BASE_URL}/eventos/copia-invitacion/${evento.value.token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ copia: evento.value.copia_invitacion_emisor ? '1' : '0' })
    })
    const result = await res.json()
    if (result.status === 'ok') {
      toast.success('Se actualizó la preferencia de copia al emisor.')
    } else {
      throw new Error('Respuesta no OK')
    }
  } catch (e) {
    toast.error('No se pudo actualizar la preferencia.')
    evento.value.copia_invitacion_emisor = !evento.value.copia_invitacion_emisor
  }
}

function safeJsonParse(str) {
  try { return JSON.parse(str) } catch { return null }
}

function humanizeLabel(label) {
  if (!label) return ''
  const s = String(label).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  return s.split(' ').map(w => (w ? w[0].toUpperCase() + w.slice(1) : '')).join(' ')
}

function normalizeOpciones(opt) {
  if (Array.isArray(opt)) return opt.map(x => String(x).trim()).filter(Boolean)
  if (typeof opt === 'string') {
    // si algún día viene "a,b,c", lo soportamos
    return opt.split(',').map(x => x.trim()).filter(Boolean)
  }
  return []
}

function normalizeRuleItem(item, fallbackIndex = 0) {
  const label = item?.label || item?.name_campo || item?.name || ''
  const ordenRaw = item?.orden ?? item?.orden_visual ?? null
  const orden = Number.isFinite(Number(ordenRaw)) && Number(ordenRaw) > 0
    ? Number(ordenRaw)
    : (fallbackIndex + 1)

  return {
    id_campo: item?.id_campo ?? null,
    label,
    nombre_del_campo: item?.nombre_del_campo || item?.nombre || item?.display || '',
    nombre_del_campo_en: item?.nombre_del_campo_en || item?.nombre_en || item?.display_en || '',
    tipo_campo: item?.tipo_campo || item?.tipo || 'texto',
    tipo_ui: item?.tipo_ui || item?.ui || 'input',
    opciones: normalizeOpciones(item?.opciones || item?.options),
    activo: !!item?.activo,
    requerido: !!item?.requerido,
    mandatorio: !!item?.mandatorio,
    custom: !!item?.custom || !!item?.is_custom,
    orden
  }
}

function sortByConfiguredOrder(list, fallbackByArrayOrder = true) {
  return (Array.isArray(list) ? list : [])
    .map((item, idx) => ({ item: { ...item }, idx }))
    .sort((a, b) => {
      const ao = Number(a.item?.orden)
      const bo = Number(b.item?.orden)
      const aHas = Number.isFinite(ao) && ao > 0
      const bHas = Number.isFinite(bo) && bo > 0

      if (aHas && bHas && ao !== bo) return ao - bo
      if (aHas !== bHas) return aHas ? -1 : 1
      return fallbackByArrayOrder ? (a.idx - b.idx) : 0
    })
    .map((entry, idx) => ({ ...entry.item, orden: idx + 1 }))
}

function mergeCampos(baseList, reglasList) {
  const base = Array.isArray(baseList) ? baseList.map((c, idx) => ({ ...c, orden: idx + 1 })) : []
  const reglas = Array.isArray(reglasList)
    ? sortByConfiguredOrder(reglasList.map((r, idx) => normalizeRuleItem(r, idx)))
    : []

  const baseMap = new Map()
  base.forEach(c => {
    baseMap.set(c.name_campo, { ...c })
  })

  const usados = new Set()
  const ordered = []

  for (const r of reglas) {
    const key = r.label
    if (!key) continue

    if (baseMap.has(key)) {
      const current = baseMap.get(key)
      ordered.push({
        ...current,
        nombre_del_campo: r.nombre_del_campo || current.nombre_del_campo,
        nombre_del_campo_en: r.nombre_del_campo_en || current.nombre_del_campo_en || '',
        tipo_campo: r.tipo_campo || current.tipo_campo,
        tipo_ui: r.tipo_ui || current.tipo_ui || 'input',
        opciones: (r.tipo_ui && (r.tipo_ui === 'select' || r.tipo_ui === 'multicheck'))
          ? (r.opciones || [])
          : (current.opciones || []),
        activo: r.activo,
        requerido: r.requerido,
        mandatorio: r.mandatorio,
        is_custom: false,
        orden: r.orden,
      })
      usados.add(key)
      continue
    }

    ordered.push({
      id_campo: r.id_campo ?? `custom_${key}`,
      nombre_del_campo: r.nombre_del_campo || humanizeLabel(key),
      nombre_del_campo_en: r.nombre_del_campo_en || '',
      name_campo: key,
      tipo_campo: r.tipo_campo || 'texto',
      tipo_ui: r.tipo_ui || 'input',
      opciones: r.opciones || [],
      activo: r.activo,
      requerido: r.requerido,
      mandatorio: r.mandatorio,
      is_custom: true,
      orden: r.orden,
    })
    usados.add(key)
  }

  for (const baseField of base) {
    if (usados.has(baseField.name_campo)) continue
    ordered.push({ ...baseField })
  }

  return sortByConfiguredOrder(ordered)
}

async function fetchCamposRegistro() {
  try {
    const res = await fetch(`${BASE_URL}/campos-registro`)
    const data = await res.json()

    const base = (data?.data || []).map((campo, idx) => ({
      ...campo,
      activo: campo.activo === '1',
      requerido: false,
      mandatorio: false,
      is_custom: false,
      orden: idx + 1,

      // defaults nuevos
      tipo_ui: campo.tipo_ui || 'input',
      opciones: Array.isArray(campo.opciones) ? campo.opciones : []
    }))

    camposRegistroBase.value = base
    camposRegistro.value = base.map(c => ({ ...c }))
  } catch (error) {
    toast.error('Error al cargar los campos del registro: ' + error.message)
  }
}

function onSaved({ response }) {
  const msg = response?.message || response?.status || 'Cambios guardados correctamente'
  toast.success(msg)
  fetchEvento()
}

function onSaveError(message) {
  toast.error(message || 'No se pudieron guardar los cambios del evento')
}

async function fetchEvento() {
  try {
    const res = await fetch(`${BASE_URL}/eventos/${route.params.token}`)
    const data = await res.json()

    evento.value = {
      ...evento.value,
      ...data,
      permitir_duplicados: data.permitir_duplicados === '1',
      copia_invitacion_emisor: data.copia_invitacion_emisor === '1',
      permitir_ingreso_publico:
        data.permitir_ingreso_publico === true ||
        data.permitir_ingreso_publico === 1 ||
        data.permitir_ingreso_publico === '1',
      gestion_empresas_pagos:
        data.gestion_empresas_pagos === true ||
        data.gestion_empresas_pagos === 1 ||
        data.gestion_empresas_pagos === '1',
      sin_limites_carga_usuarios:
        data.sin_limites_carga_usuarios === true ||
        data.sin_limites_carga_usuarios === 1 ||
        data.sin_limites_carga_usuarios === '1',
      fecha_inicio: (data.fecha_inicio || '').slice(0, 16),
      fecha_fin: (data.fecha_fin || '').slice(0, 16),
      api: data.api || evento.value.api
    }

    if (data.campos_obligatorios && data.campos_obligatorios !== 'null') {
      const parsed = safeJsonParse(data.campos_obligatorios)
      if (Array.isArray(parsed)) {
        const inherited = new Set((data.campos_heredados || []).map((label) => String(label)))
        camposRegistro.value = mergeCampos(camposRegistroBase.value, parsed).map((campo) => ({
          ...campo,
          heredado: inherited.has(String(campo.name_campo || ''))
        }))
      } else {
        camposRegistro.value = camposRegistroBase.value.map(c => ({ ...c }))
        toast.error('campos_obligatorios tiene un JSON inválido. Se cargó la lista base.')
      }
    } else {
      camposRegistro.value = camposRegistroBase.value.map(c => ({ ...c }))
    }

    if (!evento.value.permitir_duplicados) camposVisible.value = true
  } catch (error) {
    toast.error('Error al cargar el evento: ' + error.message)
  }
}

onMounted(async () => {
  await fetchCamposRegistro()
  await fetchEvento()
  isLoaded.value = true
})
</script>

<style scoped lang="scss">
.switch-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
}
.switch-bg {
  width: 4rem;
  height: 2rem;
  border-radius: 9999px;
  transition: background-color 0.2s;
}
.switch-dot {
  width: 1.5rem;
  height: 1.5rem;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 0.25rem;
  transition: transform 0.2s;
}

.col-12 {
  display: flex;
  justify-content: space-between;
}
.mensaje-personalizado {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.5);
  padding: 20px;
  border-radius: 20px;
  margin-bottom: 20px;

  h2 {
    font-weight: bold;
    font-size: 1.5em;
    margin: 20px auto;
    text-align: center;
  }
  .switch-container {
    justify-content: center;
  }
}
</style>
